import { ref } from 'vue-demi';
import { createSharedComposable, useEventBus, noop } from '@vueuse/core';

var PositionsX;
(function (PositionsX) {
  PositionsX["Right"] = "right";
  PositionsX["Left"] = "left";
  PositionsX["Center"] = "center";
})(PositionsX || (PositionsX = {}));
var PositionsY;
(function (PositionsY) {
  PositionsY["Top"] = "top";
  PositionsY["Bottom"] = "bottom";
})(PositionsY || (PositionsY = {}));
var TransitionType;
(function (TransitionType) {
  TransitionType["Slide"] = "slide";
  TransitionType["Fade"] = "fade";
})(TransitionType || (TransitionType = {}));
var ToastType;
(function (ToastType) {
  ToastType["Success"] = "success";
  ToastType["Error"] = "error";
  ToastType["Loading"] = "loading";
})(ToastType || (ToastType = {}));

var props = /*#__PURE__*/Object.freeze({
  __proto__: null,
  get PositionsX () { return PositionsX; },
  get PositionsY () { return PositionsY; },
  get TransitionType () { return TransitionType; },
  get ToastType () { return ToastType; }
});

function useQueue(initialValue) {
  //generic needed! see https://github.com/vuejs/core/issues/2136
  const queue = ref(initialValue || []);
  const addItem = item => {
    const refItem = ref(item);
    queue.value.push(refItem);
  };
  const removeItem = condition => {
    const index = queue.value.findIndex((i, idx, q) => {
      return condition === null || condition === void 0 ? void 0 : condition(i.value, idx, q);
    });
    if (index !== -1) queue.value.splice(index, 1);
  };
  const getItem = condition => {
    return queue.value.filter((i, idx, q) => {
      return condition === null || condition === void 0 ? void 0 : condition(i.value, idx, q);
    })[0];
  };
  const clearAll = () => {
    queue.value = [];
  };
  return {
    queue,
    addItem,
    removeItem,
    clearAll,
    getItem
  };
}

const useToastEventBus = () => {
  const busPull = {
    toastNotify: useEventBus('toastNotify'),
    toastClose: useEventBus('toastClose'),
    toastCloseAll: useEventBus('toastCloseAll'),
    toastUpdate: useEventBus('toastUpdate')
  };
  return {
    on: (namedQueue, listener) => {
      busPull[namedQueue].on(listener);
    },
    off: (namedQueue, listener) => {
      busPull[namedQueue].off(listener);
    },
    emit: (namedQueue, data) => {
      busPull[namedQueue].emit(data);
    }
  };
};
var useToastEventBus$1 = createSharedComposable(useToastEventBus);

class Timer {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  constructor() {
    this.startedAt = null;
    this.timer = null;
    this.callback = noop;
    this.delay = 0;
  }
  start(callback, delay) {
    if (delay && delay > 0) {
      this.callback = callback;
      this.delay = delay;
      this.startedAt = Date.now();
      this.timer = setTimeout(this.callback, this.delay);
    }
  }
  pause() {
    if (this.timer && this.startedAt) {
      this.stop();
      this.delay -= Date.now() - this.startedAt;
    }
  }
  resume() {
    this.stop();
    if (this.delay > 0) {
      this.startedAt = Date.now();
      this.timer = setTimeout(this.callback, this.delay);
    }
  }
  stop() {
    if (this.timer) clearTimeout(this.timer);
  }
}

function useToast(globalToastProps = {}) {
  const {
    emit
  } = useToastEventBus$1();
  function notify(props, behaviour = {}) {
    const {
      duration = 5000,
      pauseOnHover = true,
      onToastClose,
      onToastClick
    } = behaviour;
    const propsData = {
      ...props,
      ...globalToastProps
    };
    const config = {
      toastProps: propsData,
      //generate ToastHash
      hash: Math.random().toString(36).slice(2),
      ...(Number.isInteger(duration) && {
        timer: new Timer()
      }),
      toastBehaviour: {
        duration,
        pauseOnHover,
        onToastClose,
        onToastClick
      }
    };
    emit('toastNotify', config);
    return {
      close: () => emit('toastClose', config),
      update: data => emit('toastUpdate', {
        ...config,
        toastProps: {
          ...config.toastProps,
          ...data
        }
      })
    };
  }
  function clearAll() {
    emit('toastCloseAll');
  }
  function success(props, behaviour = {}) {
    return notify({
      ...props,
      toastType: ToastType.Success
    }, behaviour);
  }
  function error(props, behaviour = {}) {
    return notify({
      ...props,
      toastType: ToastType.Error
    }, behaviour);
  }
  function loading(props, behaviour = {}) {
    return notify({
      dismissible: false,
      ...props,
      toastType: ToastType.Loading
    }, {
      ...behaviour,
      duration: 0 // <- toast with type "loading" will not cloe automatically
    });
  }
  return {
    notify,
    clearAll,
    success,
    error,
    loading
  };
}

export { PositionsX as P, ToastType as T, useQueue as a, PositionsY as b, TransitionType as c, useToastEventBus$1 as d, props as p, useToast as u };
